import { apiGet, apiPost, apiPut, apiDelete } from '../../generic/Api_Functions'
import SmartMateriaUITable from './../../generic/SmartMateriaUITable'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueDescription, systemValueLabel } from '../../generic/systemValues/systemValueLabel'

const MaterialGroups = () => {
  const { t_ } = useSmartTranslation()
  const columns = [
    {
      name: t_('Code'),
      field: 'code',
      charLimit: 60,
      mandatory: true,
      unique: true,
      readOnly: (row) => {
        return row.systemDefault
      }
    },
    {
      name: t_('Name'),
      field: 'name',
      render: (row) => systemValueLabel(t_, 'material_group', row),
      renderOnlyInReadMode: true,
      charLimit: 60,
      mandatory: true,
      unique: true,
      readOnly: (row) => {
        return row.systemDefault
      }
    },
    {
      name: t_('Description'),
      field: 'description',
      charLimit: 200,
      // P10: a seeded row's description is factory text and reads translated; a customer row keeps its own.
      render: (row) => systemValueDescription(t_, 'material_group', row),
      renderOnlyInReadMode: true
    },
    {
      name: t_('Material class'),
      field: 'materialClass.code',
      type: 'select',
      edit: {
        source: async () => {
          return await apiGet('materialClasses')
        },
        id: 'id',
        field: 'code'
      },
      readOnly: (row) => {
        return row.systemDefault
      }
    }
  ]

  // The backend DTO's `materialClass` field is a Long id, not an object. The select column
  // works against `materialClass.code`, so we translate at the boundaries: the incoming id is
  // expanded into a { id, code } object for display, and the edited object is collapsed back
  // to its id when saving.
  const dataFetch = async (pagination) => {
    const [materialGroups, materialClasses] = await Promise.all([
      apiGet('materialGroups', pagination),
      apiGet('materialClasses')
    ])
    const classById = new Map(materialClasses.map((materialClass) => [materialClass.id, materialClass]))
    const withMaterialClass = (group) => ({
      ...group,
      materialClass: classById.get(group.materialClass) || {}
    })
    return {
      ...materialGroups,
      content: materialGroups.content.map(withMaterialClass)
    }
  }

  const toMaterialGroupDto = (row) => {
    return { ...row, materialClass: row.materialClass?.id }
  }

  const actions = {
    edit: {
      fetchFunction: (row) => {
        return apiPut('materialGroup', toMaterialGroupDto(row))
      }
    },
    create: {
      fetchFunction: (row) => {
        return apiPost('materialGroup', toMaterialGroupDto(row))
      }
    },
    delete: {
      fetchFunction: (rows) => {
        if (rows.length === 1) {
          return apiDelete('materialGroup/' + rows[0].id, rows)
        } else {
          return apiDelete('materialGroupMulti', rows)
        }
      },
      visibilityButton: (row) => {
        return !row.systemDefault
      }
    }
  }

  return (
    <SmartMateriaUITable
      tableTuneUniqueKey='materialGroup'
      columns={columns}
      title={t_('Material groups')}
      dataFetch={dataFetch}
      actions={actions}
      pageable
      multipleSelection
    />
  )
}

export default MaterialGroups
